import * as ko from "knockout";

import { ComparablePrimitiveValue } from "./commonTypes";

/**
 * An entity that can be enumerated using for..in.
 */
export type Enumerable = Object | Array<any>;

/**
 * A function that returns a comparable value from a complex object.
 */
export interface KeyProvider<TKeyedObject> {
    (current: TKeyedObject): ComparablePrimitiveValue;
}

/**
 * Searches for an element in a sorted collection by an item key.
 *
 * @param key
 *     Item key to search for.
 * @param collection
 *     Array or observable array to search.
 * @param keyProvider
 *     - When null (default), "key" is compared directly against each traversed array element (identity)
 *     - When string, "key" is compared against each traversed array element's property with this name
 *     - When function, "key" is compared against return value of this function for each traversed array element
 * @returns
 *     Index of the found element or a negative value (the insertion point) if no element was found.
 */
export function binarySearch<TKey extends ComparablePrimitiveValue, TKeyedObject>(
    key: TKey,
    collection: ko.MaybeObservableArray<TKeyedObject>,
    keyProvider: string | KeyProvider<TKeyedObject> = null): number
{
    const unwrappedCollection = ko.unwrap(collection);
    const keyProviderFn = (keyProvider === null
        ? (t: any) => t
        : (typeof keyProvider === "string"
            ? (o: any) => o[keyProvider]
            : keyProvider));

    let left = 0; // inclusive
    let right = collection.length; // exclusive
    let found: boolean;

    while (left < right) {
        const middle = (left + right) >> 1;
        const middleKey = keyProviderFn(unwrappedCollection[middle]);
        const compareResult = key > middleKey
            ? 1
            : key < middleKey ? -1 : 0;

        if (compareResult > 0) {
            left = middle + 1;
        } else {
            right = middle;
            // We are looking for the lowest index so we can't return immediately.
            found = !compareResult;
        }
    }
    // left is the index if found, or the insertion point otherwise.
    return found ? left : -left - 1;
}

/**
 * Returns true if the given enumerable is empty; false otherwise.
 */
export function isEmpty(e: Enumerable): boolean {
    for (const k in e) { if (e.hasOwnProperty(k)) { return false; } }
    return true;
}



// WEBPACK FOOTER //
// /Users/johng/dev/web-pli/util/array.ts